// ForgotPasswordPage — public form to request a password-reset email.
// Backend: POST /api/auth/password-reset/request (rate-limited 3/15min/IP).
// Always returns a neutral success message — no enumeration leak.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, Loader2, CheckCircle2, AlertCircle, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { API_URL } from '../utils/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setError('Veuillez saisir une adresse email valide.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/auth/password-reset/request`,
        { email: email.trim().toLowerCase() },
        { timeout: 10000 });
      setSubmitted(true);
    } catch (err) {
      if (err?.response?.status === 429) {
        setError('Trop de tentatives. Réessayez dans 15 minutes.');
      } else {
        setError('Erreur de connexion. Réessayez dans un instant.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/login" data-testid="forgot-back-to-login"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-indigo-600 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
        </Link>

        <div className="bg-white rounded-3xl shadow-xl border border-slate-200/60 p-7">
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-slate-900 font-['Outfit'] leading-tight">Mot de passe oublié&nbsp;?</h1>
              <p className="text-[11px] text-slate-500">On vous envoie un lien sécurisé.</p>
            </div>
          </div>

          {submitted ? (
            <div data-testid="forgot-success" className="space-y-3">
              <div className="flex items-start gap-2 p-3.5 rounded-xl bg-emerald-50 border border-emerald-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-emerald-800">
                  <p className="font-semibold mb-1">Email envoyé</p>
                  <p className="leading-relaxed">
                    Si un compte existe pour cette adresse, un lien de réinitialisation
                    valide <strong>1 heure</strong> vient de partir. Pensez à vérifier vos spams.
                  </p>
                </div>
              </div>
              <Link to="/login"
                className="block text-center w-full h-9 leading-9 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white font-medium text-sm hover:from-indigo-600 hover:to-violet-600">
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <p className="text-xs text-slate-600 leading-relaxed">
                Saisissez l'adresse email associée à votre compte. Si elle existe,
                vous recevrez un email avec un lien de réinitialisation valide 1&nbsp;heure.
              </p>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-slate-700 font-medium text-xs">Adresse email</Label>
                <div className="relative">
                  <Mail className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="vous@exemple.fr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                    data-testid="forgot-email-input"
                    className="h-9 pl-8 rounded-lg bg-slate-50 border-slate-200 text-sm placeholder:text-slate-400 focus:bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20"
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-1.5 p-2 rounded-lg bg-rose-50 border border-rose-200 text-[11px] text-rose-700">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit-btn"
                className="w-full h-9 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white font-medium text-sm shadow-sm">
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Envoi...</>
                ) : 'Envoyer le lien'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
